/**
 * §7.3 — Swarm movement model. Weighted boids blend integrated at the fixed 60 Hz
 * step: separation, alignment, cohesion, pursuit, avoidance, formation-goal.
 *
 *   desired = Σ wi · Si
 *   accel   = clamp(desired · max_speed − v, |·| ≤ max_force)
 *   v      += accel · dt;  |v| ≤ max_speed;  heading change ≤ turn_rate · dt
 *
 * Neighbour queries go through the §14.4 spatial hash with a 6 u radius and a hard
 * 32-candidate cap. The pool is never scanned. AI jitter consumes `w.rng.ai` only.
 * Zero allocation: every scratch buffer is module scope.
 */

import type { World } from './world';
import type { Enemy } from './entities';
import { wrapDeltaX, wrapDeltaY } from './wrap';

/** §7.3 neighbour query radius. */
export const NEIGHBOUR_RADIUS = 6;
/** §14.4 query budget — hard cap on candidates inspected per enemy per tick. */
export const NEIGHBOUR_CAP = 32;
/** §7.4 overlap cap: centre distance ≥ 0.7 × (r1 + r2). */
export const SEPARATION_FLOOR_K = 0.7;
/** Separation steering falls off over 3 u (§7.3 "push from neighbors < 3 u"). */
export const SEPARATION_RANGE = 3;
/** Largest enemy hitbox radius the separation query must be able to reach. */
export const MAX_ENEMY_RADIUS = 3;
/** Gauss-Seidel relaxation passes used to hold the §7.4 floor. */
export const SEPARATION_ITERATIONS = 3;
/** Wander re-roll period in ticks; staggered by pool index so the `ai` draw order is stable. */
const WANDER_PERIOD = 12;
const WANDER_WEIGHT = 0.18;
/**
 * §8.10 hunt shaping. At full hunt the pursuit term is tripled and the wander is silenced,
 * so the blend commits to the chase instead of schooling. Speed alone does not close a gap:
 * a driftfin runs at 91% of its max speed on an outrun line and still loses ground, because
 * pursuit is 1.0 of a 4.9 weight sum and the normalised wish vector points mostly sideways.
 */
const HUNT_PURSUIT_GAIN = 2;
const TAU = Math.PI * 2;
const PI = Math.PI;

const cand = new Int32Array(NEIGHBOUR_CAP);
const sepCand = new Int32Array(NEIGHBOUR_CAP);

/** Cached hazard discs to avoid, refreshed once per tick by `beginSteerFrame`. */
const AVOID_CAP = 64;
const avoidX = new Float64Array(AVOID_CAP);
const avoidY = new Float64Array(AVOID_CAP);
const avoidR = new Float64Array(AVOID_CAP);
let avoidCount = 0;

/** Diagnostics for tests and the §14 perf overlay. Never read by sim logic. */
export const steerDiag = {
  queries: 0,
  maxCandidates: 0,
  truncatedQueries: 0,
  separationChecks: 0,
  separationViolations: 0,
  neighboursUsedMax: 0,
};

export function resetSteerDiag(): void {
  steerDiag.queries = 0;
  steerDiag.maxCandidates = 0;
  steerDiag.truncatedQueries = 0;
  steerDiag.separationChecks = 0;
  steerDiag.separationViolations = 0;
  steerDiag.neighboursUsedMax = 0;
}

/** Call once per sim tick before stepping enemies. Caches hazard discs for avoidance. */
export function beginSteerFrame(w: World): void {
  avoidCount = 0;
  const list = w.hazards.liveIndices();
  for (let i = 0; i < list.length; i++) {
    if (avoidCount >= AVOID_CAP) break;
    const h = w.hazards.slots[list[i]];
    if (!h.alive) continue;
    if (w.tick > h.end_tick) continue;
    avoidX[avoidCount] = h.pos.x;
    avoidY[avoidCount] = h.pos.y;
    avoidR[avoidCount] = h.zone.shape === 'circle' ? h.zone.r : Math.max(h.zone.w, h.zone.h) * 0.5;
    avoidCount++;
  }
}

function wrapAngle(a: number): number {
  if (a > PI) return a - TAU;
  if (a < -PI) return a + TAU;
  return a;
}

/**
 * One blended-steering integration step for `e`. Weights come from the archetype
 * def (§7.3 table). `pursuitLead` is the archetype's position prediction in seconds.
 * Position integration is done by the caller so scripted states can bypass this.
 *
 * `hunt` is the §8.10 hunt factor in [0,1] computed by `enemies.ts`. At 0 this function is
 * bit-for-bit what it was before the hunt ramp existed.
 */
export function steerEnemy(
  w: World,
  e: Enemy,
  wSep: number,
  wAli: number,
  wCoh: number,
  wPur: number,
  wAvo: number,
  wForm: number,
  maxSpeed: number,
  maxForce: number,
  turnRateRad: number,
  pursuitLead: number,
  goalX: number,
  goalY: number,
  hunt: number,
  dt: number
): void {
  const wPurH = hunt > 0 ? wPur * (1 + HUNT_PURSUIT_GAIN * hunt) : wPur;
  let sx = 0;
  let sy = 0;
  let ax = 0;
  let ay = 0;
  let cx = 0;
  let cy = 0;
  let nFlock = 0;

  if (wSep > 0 || wAli > 0 || wCoh > 0) {
    const n = w.hash.queryCircle(e.pos.x, e.pos.y, NEIGHBOUR_RADIUS, cand, NEIGHBOUR_CAP);
    steerDiag.queries++;
    if (n > steerDiag.maxCandidates) steerDiag.maxCandidates = n;
    if (n >= NEIGHBOUR_CAP) steerDiag.truncatedQueries++;
    const stamp = w.nextStamp();
    w.seenStamp[e.pool_index] = stamp;
    for (let i = 0; i < n; i++) {
      const idx = cand[i];
      if (w.seenStamp[idx] === stamp) continue;
      w.seenStamp[idx] = stamp;
      const o = w.enemies.slots[idx];
      if (!o.alive || o.death_anim > 0) continue;
      const dx = wrapDeltaX(e.pos.x - o.pos.x);
      const dy = wrapDeltaY(e.pos.y - o.pos.y);
      const d2 = dx * dx + dy * dy;
      if (d2 > NEIGHBOUR_RADIUS * NEIGHBOUR_RADIUS || d2 < 1e-9) continue;
      const d = Math.sqrt(d2);
      if (d < SEPARATION_RANGE) {
        const k = (SEPARATION_RANGE - d) / SEPARATION_RANGE / d;
        sx += dx * k;
        sy += dy * k;
      }
      ax += o.vel.x;
      ay += o.vel.y;
      // Cohesion accumulates wrapped OFFSETS from `e`, never absolute positions: a flock
      // straddling the seam would otherwise average to a centroid on the far side (AMD-24).
      cx -= dx;
      cy -= dy;
      nFlock++;
    }
    if (nFlock > steerDiag.neighboursUsedMax) steerDiag.neighboursUsedMax = nFlock;
  }

  let dvx = 0;
  let dvy = 0;

  if (wSep > 0) {
    const l = Math.sqrt(sx * sx + sy * sy);
    if (l > 1e-6) {
      dvx += (sx / l) * wSep;
      dvy += (sy / l) * wSep;
    }
  }
  if (wAli > 0 && nFlock > 0) {
    const l = Math.sqrt(ax * ax + ay * ay);
    if (l > 1e-6) {
      dvx += (ax / l) * wAli;
      dvy += (ay / l) * wAli;
    }
  }
  if (wCoh > 0 && nFlock > 0) {
    const tx = cx / nFlock;
    const ty = cy / nFlock;
    const l = Math.sqrt(tx * tx + ty * ty);
    if (l > 1e-6) {
      dvx += (tx / l) * wCoh;
      dvy += (ty / l) * wCoh;
    }
  }
  if (wPurH > 0) {
    const tx = wrapDeltaX(w.player.pos.x + w.player.vel.x * pursuitLead - e.pos.x);
    const ty = wrapDeltaY(w.player.pos.y + w.player.vel.y * pursuitLead - e.pos.y);
    const l = Math.sqrt(tx * tx + ty * ty);
    if (l > 1e-6) {
      dvx += (tx / l) * wPurH;
      dvy += (ty / l) * wPurH;
    }
  }
  if (wAvo > 0) {
    // No wall term: the arena is a torus (AMD-24), so avoidance is hazard discs only.
    let vx = 0;
    let vy = 0;
    for (let i = 0; i < avoidCount; i++) {
      const dx = wrapDeltaX(e.pos.x - avoidX[i]);
      const dy = wrapDeltaY(e.pos.y - avoidY[i]);
      const rr = avoidR[i] + 2;
      const d2 = dx * dx + dy * dy;
      if (d2 >= rr * rr || d2 < 1e-9) continue;
      const d = Math.sqrt(d2);
      const k = (rr - d) / rr / d;
      vx += dx * k;
      vy += dy * k;
    }
    const l = Math.sqrt(vx * vx + vy * vy);
    if (l > 1e-6) {
      dvx += (vx / l) * wAvo;
      dvy += (vy / l) * wAvo;
    }
  }
  if (wForm > 0) {
    const tx = wrapDeltaX(goalX - e.pos.x);
    const ty = wrapDeltaY(goalY - e.pos.y);
    const l = Math.sqrt(tx * tx + ty * ty);
    if (l > 0.5) {
      dvx += (tx / l) * wForm;
      dvy += (ty / l) * wForm;
    }
  }

  // §7.3 AI jitter — `ai` stream only, re-rolled on a staggered period so the
  // draw order stays a pure function of tick and pool index.
  if (((w.tick + e.pool_index) % WANDER_PERIOD) === 0) {
    e.steering.wander = w.rng.ai.next() * 2 - 1;
  }
  // The draw above is unconditional — gating it on `hunt` would move the `ai` cursor and
  // break replay. Only the applied magnitude fades, so a hunting body stops wandering.
  if (e.steering.wander !== 0 && hunt < 1) {
    const ww = WANDER_WEIGHT * (1 - hunt);
    dvx += -Math.sin(e.steering.heading) * e.steering.wander * ww;
    dvy += Math.cos(e.steering.heading) * e.steering.wander * ww;
  }

  const dl = Math.sqrt(dvx * dvx + dvy * dvy);
  let tvx = 0;
  let tvy = 0;
  if (dl > 1e-6) {
    tvx = (dvx / dl) * maxSpeed;
    tvy = (dvy / dl) * maxSpeed;
  }
  let fx = tvx - e.vel.x;
  let fy = tvy - e.vel.y;
  const fl = Math.sqrt(fx * fx + fy * fy);
  if (fl > maxForce && fl > 1e-9) {
    const s = maxForce / fl;
    fx *= s;
    fy *= s;
  }
  e.vel.x += fx * dt;
  e.vel.y += fy * dt;

  let sp2 = e.vel.x * e.vel.x + e.vel.y * e.vel.y;
  if (sp2 > maxSpeed * maxSpeed) {
    const s = maxSpeed / Math.sqrt(sp2);
    e.vel.x *= s;
    e.vel.y *= s;
    sp2 = maxSpeed * maxSpeed;
  }
  applyTurnLimit(e, sp2, turnRateRad, dt);
}

/** Clamps the heading change to turn_rate·dt and re-projects velocity onto it (§7.3). */
export function applyTurnLimit(e: Enemy, sp2: number, turnRateRad: number, dt: number): void {
  if (sp2 < 1e-8) return;
  const sp = Math.sqrt(sp2);
  const want = Math.atan2(e.vel.y, e.vel.x);
  let d = wrapAngle(want - e.steering.heading);
  const maxD = turnRateRad * dt;
  if (d > maxD) d = maxD;
  else if (d < -maxD) d = -maxD;
  const h = wrapAngle(e.steering.heading + d);
  e.steering.heading = h;
  e.vel.x = Math.cos(h) * sp;
  e.vel.y = Math.sin(h) * sp;
}

/**
 * §7.4 rule 1 — positional relaxation so no two enemy centres sit closer than
 * 0.7 × (r1 + r2). Gauss-Seidel over a small query radius; each entity corrects
 * itself by the full deficit, so a processed pair always satisfies the floor.
 * Runs after integration; call `resolveSeparationPass` once per iteration.
 */
export function resolveSeparationPass(w: World): number {
  let violations = 0;
  const list = w.enemies.liveIndices();
  for (let i = 0; i < list.length; i++) {
    const e = w.enemies.slots[list[i]];
    if (!e.alive || e.death_anim > 0) continue;
    const qr = SEPARATION_FLOOR_K * (e.hitbox_radius + MAX_ENEMY_RADIUS);
    const n = w.hash.queryCircle(e.pos.x, e.pos.y, qr, sepCand, NEIGHBOUR_CAP);
    steerDiag.queries++;
    const stamp = w.nextStamp();
    w.seenStamp[e.pool_index] = stamp;
    for (let k = 0; k < n; k++) {
      const idx = sepCand[k];
      if (w.seenStamp[idx] === stamp) continue;
      w.seenStamp[idx] = stamp;
      const o = w.enemies.slots[idx];
      if (!o.alive || o.death_anim > 0) continue;
      steerDiag.separationChecks++;
      const floor = SEPARATION_FLOOR_K * (e.hitbox_radius + o.hitbox_radius);
      let dx = wrapDeltaX(e.pos.x - o.pos.x);
      let dy = wrapDeltaY(e.pos.y - o.pos.y);
      let d2 = dx * dx + dy * dy;
      if (d2 >= floor * floor) continue;
      violations++;
      if (d2 < 1e-8) {
        // Perfect overlap: break the tie deterministically on pool index.
        const a = (e.pool_index - o.pool_index) * 0.7853981633974483;
        dx = Math.cos(a);
        dy = Math.sin(a);
        d2 = 1;
      }
      const d = Math.sqrt(d2);
      const push = (floor - d) / d;
      e.pos.x += dx * push;
      e.pos.y += dy * push;
      w.wrapToArena(e.pos, e.hitbox_radius);
    }
  }
  return violations;
}

/** Runs the §7.4 relaxation to convergence (bounded by SEPARATION_ITERATIONS). */
export function resolveSeparation(w: World): void {
  let v = 0;
  for (let i = 0; i < SEPARATION_ITERATIONS; i++) {
    v = resolveSeparationPass(w);
    if (v === 0) break;
  }
  steerDiag.separationViolations = v;
}
